import { Component, Input, OnDestroy, Inject, ViewEncapsulation, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { NgClass } from '@angular/common';
import { SpinnerService } from '../../../services/spinner.service';
import { filter, skip, take, takeUntil } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';

@Component({
  selector: 'app-spinner',
  template: ` @if (isSpinnerVisible) {
    <div class="preloader" [ngClass]="spinnerHasWhiteBackground ? 'white-bg' : 'overlay-bg'">
      <div class="spinner">
        <div class="double-bounce1"></div>
        <div class="double-bounce2"></div>
      </div>
    </div>
  }`,
  encapsulation: ViewEncapsulation.None,
  imports: [NgClass],
})
export class SpinnerComponent implements OnDestroy {
  public isSpinnerVisible = false;
  public spinnerHasWhiteBackground = true;
  private onDestroy$ = new Subject<void>();
  private changeVisibilityValueToChange: boolean | undefined;
  private changeBackgroundValueToChange: boolean = true;
  private isWaiting = false;

  @Input() public backgroundColor = 'rgba(0, 115, 170, 0.69)';

  constructor(
    private router: Router,
    private spinnerService: SpinnerService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.spinnerService.showSpinner.pipe(skip(1)).subscribe((spinnerVisible) => {
      if (this.isWaiting) {
        // Inside the throttle window: remember only the latest request so we
        // reconcile to whatever was asked for last when the window closes.
        this.changeVisibilityValueToChange = spinnerVisible;
      } else if (spinnerVisible !== this.isSpinnerVisible) {
        this.setVisible(spinnerVisible);
      }
    });
    this.spinnerService.spinnerBackground.pipe(skip(1)).subscribe((spinnerHasWhiteBackground) => {
      this.changeBackgroundValueToChange = spinnerHasWhiteBackground;
      if (!this.isSpinnerVisible) {
        this.spinnerHasWhiteBackground = spinnerHasWhiteBackground;
      }
    });
  }

  private setVisible(visible: boolean) {
    this.isSpinnerVisible = visible;
    this.isWaiting = true;
    this.changeVisibilityValueToChange = undefined;

    if (!this.isSpinnerVisible) {
      this.spinnerHasWhiteBackground = this.changeBackgroundValueToChange;
    }

    timer(500)
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.isWaiting = false;
        const nextValue = this.changeVisibilityValueToChange;
        this.changeVisibilityValueToChange = undefined;
        // Reconcile to the last requested value; open a new window if it changed
        // so state can never latch on a stale value.
        if (nextValue !== undefined && nextValue !== this.isSpinnerVisible) {
          this.setVisible(nextValue);
        }
      });
  }

  ngOnDestroy(): void {
    this.isSpinnerVisible = false;
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
